<template>
  <div class="dashboard-shell">
    <template v-if="showCombinedDashboard">
      <div class="combined-dashboard">
        <section class="combined-dashboard__hero">
          <div class="combined-dashboard__hero-kicker">Multi Role Workspace</div>
          <h1 class="combined-dashboard__hero-title">Combined dashboard</h1>
          <p class="combined-dashboard__hero-copy">
            The most important admin, committee, and user work is collected here so this account can move across workflows without jumping between separate dashboards.
          </p>
          <div class="combined-dashboard__hero-actions">
            <CButton color="primary" class="font-weight-bold" size="sm" @click="loadCombinedDashboard">
              Refresh
            </CButton>
            <span class="combined-dashboard__hero-meta">Last updated: {{ formatRefreshTime(lastUpdatedAt) }}</span>
          </div>
        </section>

        <section class="combined-stats">
          <CCard v-for="item in combinedStats" :key="item.label" class="combined-stat-card border-0 shadow-sm">
            <CCardBody>
              <div class="combined-stat-card__label">{{ item.label }}</div>
              <div class="combined-stat-card__value">{{ item.value }}</div>
              <div class="combined-stat-card__meta">{{ item.meta }}</div>
            </CCardBody>
          </CCard>
        </section>

        <section class="combined-panels">
          <CCard v-if="hasRole('admin')" class="combined-panel border-0 shadow-sm">
            <CCardHeader class="combined-panel__header bg-white border-0">
              <div>
                <div class="combined-panel__eyebrow">Administrator</div>
                <div class="combined-panel__title">Admin Focus</div>
                <div class="combined-panel__copy">The next admin actions that matter most right now.</div>
              </div>
            </CCardHeader>
            <CCardBody class="pt-0">
              <div class="combined-panel__summary">
                <div class="combined-panel__metric">
                  <span>Pending reviews</span>
                  <strong>{{ adminPendingReviews }}</strong>
                </div>
                <div class="combined-panel__metric">
                  <span>Total agreements</span>
                  <strong>{{ adminTotalAgreements }}</strong>
                </div>
              </div>
              <div v-if="adminQueueItems.length" class="combined-list">
                <button v-for="item in adminQueueItems" :key="item.id" class="combined-list__item" @click="openAdminReview(item.id)">
                  <strong>{{ item.title }}</strong>
                  <span>{{ item.meta }}</span>
                </button>
              </div>
              <div v-else class="combined-empty">No admin queue items need attention right now.</div>
              <div class="combined-actions">
                <CButton color="secondary" variant="outline" size="sm" @click="goTo('/agreements')">All Agreements</CButton>
                <CButton color="secondary" variant="outline" size="sm" @click="goTo('/review-queue')">Review Queue</CButton>
              </div>
            </CCardBody>
          </CCard>

          <CCard v-if="hasRole('committee')" class="combined-panel border-0 shadow-sm">
            <CCardHeader class="combined-panel__header bg-white border-0">
              <div>
                <div class="combined-panel__eyebrow">Committee</div>
                <div class="combined-panel__title">Committee Focus</div>
                <div class="combined-panel__copy">Urgent deadlines and active reviews in the committee queue.</div>
              </div>
            </CCardHeader>
            <CCardBody class="pt-0">
              <div v-if="committeeUrgentItem" class="combined-urgent">
                <div class="combined-urgent__eyebrow">Urgent Deadline</div>
                <strong class="combined-urgent__title">{{ committeeUrgentItem.agreement }}</strong>
                <div class="combined-urgent__copy">{{ formatDaysLeft(committeeUrgentItem.daysLeft) }} remaining</div>
              </div>
              <div class="combined-panel__summary">
                <div class="combined-panel__metric">
                  <span>Due soon</span>
                  <strong>{{ committeeDueSoon }}</strong>
                </div>
                <div class="combined-panel__metric">
                  <span>Active reviews</span>
                  <strong>{{ committeeActiveReviews }}</strong>
                </div>
              </div>
              <div v-if="committeeQueueItems.length" class="combined-list">
                <button v-for="item in committeeQueueItems" :key="item.id" class="combined-list__item" @click="openCommitteeReview(item)">
                  <strong>{{ item.agreement }}</strong>
                  <span>{{ item.partner }} · {{ formatDaysLeft(item.daysLeft) }}</span>
                </button>
              </div>
              <div v-else class="combined-empty">No committee review items are active right now.</div>
              <div class="combined-actions">
                <CButton color="secondary" variant="outline" size="sm" @click="goTo('/review')">Review</CButton>
                <CButton color="secondary" variant="outline" size="sm" @click="goTo('/review-history')">Review History</CButton>
              </div>
            </CCardBody>
          </CCard>

          <CCard v-if="hasRole('user')" class="combined-panel border-0 shadow-sm">
            <CCardHeader class="combined-panel__header bg-white border-0">
              <div>
                <div class="combined-panel__eyebrow">User</div>
                <div class="combined-panel__title">User Focus</div>
                <div class="combined-panel__copy">Personal agreement workflow items that need the next response.</div>
              </div>
            </CCardHeader>
            <CCardBody class="pt-0">
              <div class="combined-panel__summary">
                <div class="combined-panel__metric">
                  <span>In review</span>
                  <strong>{{ userPendingCount }}</strong>
                </div>
                <div class="combined-panel__metric">
                  <span>Approved</span>
                  <strong>{{ userApprovedCount }}</strong>
                </div>
              </div>
              <div v-if="userQueueItems.length" class="combined-list">
                <button v-for="item in userQueueItems" :key="item.id" class="combined-list__item" @click="openUserItem(item)">
                  <strong>{{ item.title }}</strong>
                  <span>{{ item.derivedStageLabel || item.status || 'In review' }}</span>
                </button>
              </div>
              <div v-else class="combined-empty">No user review items need follow-up right now.</div>
              <div class="combined-actions">
                <CButton color="secondary" variant="outline" size="sm" @click="goTo('/dashboard/user-agreements')">My Agreements</CButton>
                <CButton color="secondary" variant="outline" size="sm" @click="goTo('/dashboard/user-review-queue')">My Review Queue</CButton>
              </div>
            </CCardBody>
          </CCard>
        </section>
      </div>
    </template>
    <DashboardAdmin v-else-if="workspaceRole === 'admin'" />
    <DashboardCommittee v-else-if="workspaceRole === 'committee'" />
    <DashboardUser v-else />
  </div>
</template>

<script>
import Service from '@/service/api'
import { getAssignedRoles, getWorkspaceRole, workspaceRoleEventName } from '@/utils/role-dashboard'
import { isReadyForAdminFinalApproval } from '@/utils/review-final-approval'
import DashboardAdmin from '@/views/dashboard/Admin'
import DashboardCommittee from '@/views/dashboard/Committee'
import DashboardUser from '@/views/dashboard/User'

function adminStageLabel(item) {
  if (!item) return ''
  if (item.status === 'approved') return 'Approved'
  if (item.status === 'revision' || item.status === 'rejected') return 'Returned for User Revision'
  if (isReadyForAdminFinalApproval(item)) return 'Waiting for approval'
  return {
    1: 'Draft Submitted',
    2: 'Assign Committee',
    3: 'Committee Review and Comments'
  }[Math.min(Number(item.currentStep || 1), 3)] || 'Draft Submitted'
}

export default {
  name: 'Dashboard',
  components: {
    DashboardAdmin,
    DashboardCommittee,
    DashboardUser
  },
  data() {
    return {
      lastUpdatedAt: '',
      adminWorkflows: [],
      committeeDashboard: {
        stats: {
          pending: 0,
          inProgress: 0,
          completed: 0,
          dueSoon: 0
        },
        reviews: []
      },
      userReviewQueue: {
        pending: [],
        history: []
      },
      workspaceRoleVersion: 0
    }
  },
  computed: {
    assignedRoles() {
      return getAssignedRoles(this.$store)
    },
    hasMultipleRoles() {
      return this.assignedRoles.length > 1
    },
    workspaceRole() {
      this.workspaceRoleVersion
      return getWorkspaceRole(this.$store)
    },
    showCombinedDashboard() {
      return this.hasMultipleRoles && this.workspaceRole === 'multiuser'
    },
    adminPendingReviews() {
      return this.adminWorkflows.filter(item => item.status !== 'approved').length
    },
    adminTotalAgreements() {
      return this.adminWorkflows.length
    },
    adminQueueItems() {
      return this.adminWorkflows
        .filter(item => item.status !== 'approved')
        .slice()
        .sort((left, right) => Number(left.currentStep || 1) - Number(right.currentStep || 1))
        .slice(0, 3)
        .map(item => ({
          id: item.id,
          title: item.title,
          meta: `${item.type || 'Agreement'} · ${adminStageLabel(item)}`
        }))
    },
    committeeStats() {
      return Object.assign({ pending: 0, inProgress: 0, completed: 0, dueSoon: 0 }, this.committeeDashboard.stats || {})
    },
    committeeReviews() {
      return Array.isArray(this.committeeDashboard.reviews) ? this.committeeDashboard.reviews : []
    },
    committeeDueSoon() {
      return Number(this.committeeStats.dueSoon || 0)
    },
    committeeActiveReviews() {
      return this.committeeReviews.filter(item => item.status !== 'completed').length
    },
    committeeUrgentItem() {
      return this.committeeReviews.find(item => item.priority === 'high' && item.status !== 'completed') || null
    },
    committeeQueueItems() {
      return this.committeeReviews
        .filter(item => item.status !== 'completed')
        .slice(0, 3)
    },
    userPendingItems() {
      return Array.isArray(this.userReviewQueue.pending) ? this.userReviewQueue.pending : []
    },
    userHistoryItems() {
      return Array.isArray(this.userReviewQueue.history) ? this.userReviewQueue.history : []
    },
    userPendingCount() {
      return this.userPendingItems.length
    },
    userApprovedCount() {
      return this.userHistoryItems.filter(item => item.derivedStage === 'approved').length
    },
    userQueueItems() {
      return this.userPendingItems.slice(0, 3)
    },
    combinedStats() {
      const items = []
      if (this.hasRole('admin')) {
        items.push({ label: 'Admin Pending', value: this.adminPendingReviews, meta: 'Reviews waiting in admin workflow' })
      }
      if (this.hasRole('committee')) {
        items.push({ label: 'Committee Due Soon', value: this.committeeDueSoon, meta: 'Reviews approaching the 7 day deadline' })
        items.push({ label: 'Committee Active', value: this.committeeActiveReviews, meta: 'Current committee review items' })
      }
      if (this.hasRole('user')) {
        items.push({ label: 'User Review', value: this.userPendingCount, meta: 'Your agreements still moving in workflow' })
      }
      return items
    }
  },
  created() {
    if (this.showCombinedDashboard) {
      this.loadCombinedDashboard()
    }
  },
  mounted() {
    window.addEventListener(workspaceRoleEventName(), this.handleWorkspaceRoleChange)
  },
  beforeDestroy() {
    window.removeEventListener(workspaceRoleEventName(), this.handleWorkspaceRoleChange)
  },
  methods: {
    handleWorkspaceRoleChange() {
      this.workspaceRoleVersion += 1
      this.$nextTick(() => {
        if (this.showCombinedDashboard) {
          this.loadCombinedDashboard()
        }
      })
    },
    hasRole(role) {
      return this.assignedRoles.includes(role)
    },
    async loadCombinedDashboard() {
      const requests = []
      const handlers = []

      if (this.hasRole('admin')) {
        requests.push(Service.adminReview('queue'))
        handlers.push((response) => {
          this.adminWorkflows = response && response.data && Array.isArray(response.data.data) ? response.data.data : []
        })
      }

      if (this.hasRole('committee')) {
        requests.push(Service.committeeReview('dashboard'))
        handlers.push((response) => {
          const data = response && response.data && response.data.data ? response.data.data : {}
          this.committeeDashboard = {
            stats: Object.assign({ pending: 0, inProgress: 0, completed: 0, dueSoon: 0 }, data.stats || {}),
            reviews: Array.isArray(data.reviews) ? data.reviews : []
          }
        })
      }

      if (this.hasRole('user')) {
        requests.push(Service.agreements('review-queue'))
        handlers.push((response) => {
          const data = response && response.data && response.data.data ? response.data.data : {}
          this.userReviewQueue = {
            pending: Array.isArray(data.pending) ? data.pending : [],
            history: Array.isArray(data.history) ? data.history : []
          }
        })
      }

      const results = await Promise.allSettled(requests)
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          handlers[index](result.value)
        }
      })
      this.lastUpdatedAt = new Date().toISOString()
    },
    formatRefreshTime(value) {
      if (!value) return 'Just now'
      return new Date(value).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })
    },
    formatDaysLeft(daysLeft) {
      const value = Number(daysLeft || 0)
      if (value <= 0) return 'Due today'
      if (value === 1) return '1 day left'
      return `${value} days left`
    },
    goTo(path) {
      if (!path) return
      this.$router.push(path)
    },
    openAdminReview(id) {
      if (!id) return
      this.$router.push({ path: '/review', query: { agreementId: id } })
    },
    openCommitteeReview(review) {
      if (!review) return
      this.$router.push({ path: '/review', query: { id: review.agreementId || review.id } })
    },
    openUserItem(item) {
      if (!(item && item.id)) return
      if (item.derivedStage === 'submitted' || item.derivedStage === 'suggestion' || item.derivedStage === 'revision') {
        this.$router.push(`/dashboard/user-agreements/${item.id}/start-review`)
        return
      }
      this.$router.push(`/dashboard/user-agreements/${item.id}/review-summary?mode=view`)
    }
  }
}
</script>

<style scoped>
.dashboard-shell {
  display: block;
}

.combined-dashboard {
  display: grid;
  gap: 24px;
}

.combined-dashboard__hero {
  background:
    radial-gradient(circle at top right, rgba(254, 194, 96, 0.22), transparent 26%),
    linear-gradient(135deg, #5d0f12 0%, #8c1515 46%, #c77f28 100%);
  border-radius: 28px;
  box-shadow: 0 20px 40px rgba(44, 52, 71, 0.1);
  color: #fff8f1;
  padding: 28px 30px;
}

.combined-dashboard__hero-kicker {
  color: rgba(255, 248, 240, 0.82);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.combined-dashboard__hero-title {
  font-size: 2.1rem;
  font-weight: 800;
  margin: 0;
}

.combined-dashboard__hero-copy {
  color: rgba(255, 246, 237, 0.9);
  margin: 10px 0 0;
  max-width: 52rem;
}

.combined-dashboard__hero-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
}

.combined-dashboard__hero-meta {
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  color: #fff8f1;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 8px 12px;
}

.combined-stats {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
}

.combined-stat-card {
  border-radius: 20px;
}

.combined-stat-card__label {
  color: #7c6e78;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.combined-stat-card__value {
  color: #4a1d24;
  font-size: 2rem;
  font-weight: 800;
  margin-top: 8px;
}

.combined-stat-card__meta {
  color: #6b7280;
  margin-top: 8px;
}

.combined-panels {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
}

.combined-panel {
  border-radius: 22px;
}

.combined-panel__header {
  padding-bottom: 10px;
}

.combined-panel__eyebrow {
  color: #8c1515;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.combined-panel__title {
  color: #472027;
  font-size: 1.2rem;
  font-weight: 800;
  margin-top: 4px;
}

.combined-panel__copy {
  color: #6b7280;
  margin-top: 4px;
}

.combined-panel__summary {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 14px;
}

.combined-panel__metric {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 12px 14px;
}

.combined-panel__metric span {
  color: #6b7280;
  display: block;
  font-size: 0.82rem;
  margin-bottom: 6px;
}

.combined-panel__metric strong {
  color: #243b53;
  font-size: 1.35rem;
}

.combined-urgent {
  background: linear-gradient(135deg, #fff7ed 0%, #fff1f2 100%);
  border: 1px solid #fecaca;
  border-radius: 16px;
  margin-bottom: 14px;
  padding: 14px 16px;
}

.combined-urgent__eyebrow {
  color: #b42318;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.combined-urgent__title {
  color: #7f1d1d;
  display: block;
  margin-top: 4px;
}

.combined-urgent__copy {
  color: #7c2d12;
  margin-top: 4px;
}

.combined-list {
  display: grid;
  gap: 10px;
}

.combined-list__item {
  background: #fff;
  border: 1px solid #eaded4;
  border-radius: 16px;
  cursor: pointer;
  display: grid;
  gap: 4px;
  padding: 14px 16px;
  text-align: left;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.combined-list__item strong {
  color: #472027;
}

.combined-list__item span {
  color: #6b7280;
}

.combined-list__item:hover,
.combined-list__item:focus {
  border-color: #d7c0b1;
  box-shadow: 0 12px 28px rgba(49, 31, 20, 0.08);
  outline: none;
  transform: translateY(-1px);
}

.combined-empty {
  color: #6b7280;
  padding: 8px 0 12px;
}

.combined-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

@media (max-width: 767px) {
  .combined-dashboard__hero {
    padding: 22px 20px;
  }

  .combined-dashboard__hero-title {
    font-size: 1.8rem;
  }

  .combined-panel__summary {
    grid-template-columns: 1fr;
  }
}
</style>
